import { Bold, Spinner } from '@zipdrug/ui';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoginFormContainer from './LoginFormContainer';
import StandaloneFormPageTemplate from '../ui/StandaloneFormPageTemplate';
import { NODE_API_URL, API_HTTP_URL } from '../settings.js';
import SiteDownContainer from './SiteDownContainer';

const LoginPage = () => {
  const [isAnthemEmployee, setIsAnthemEmployee] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSiteDown, setIsSiteDown] = useState(false);
  const [error, setError] = useState(null);
  const [useOkta, setUseOkta] = useState(false);
  const [oktaCheckComplete, setOktaCheckComplete] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const waitForLogoutCompletion = async () => {
    // Check if we're coming from a logout by looking for logout-related URL params or referrer
    const isFromLogout = searchParams.get('logout') === 'true' || 
                        document.referrer.includes('/settings') ||
                        sessionStorage.getItem('logoutInProgress') === 'true';
    
    if (isFromLogout) {
      // Wait longer for logout operations to complete
      await new Promise(resolve => setTimeout(resolve, 5000)); // 5 second delay for logout
      sessionStorage.removeItem('logoutInProgress'); // Clean up
    } else {
      // Shorter delay for normal page loads
      await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
    }
  };

  const checkOktaFeatureFlag = async () => {
    try {
      await waitForLogoutCompletion();
      
      const root = NODE_API_URL || API_HTTP_URL.replace('/graphql', '');
      const oktaEnableUrl = `${root}/okta-enable`;
      
      const response = await fetch(oktaEnableUrl, {
        method: 'GET',
        credentials: 'omit', // Don't send cookies to avoid session re-establishment
      });
      
      if (response.ok) {
        const data = await response.json();
        setUseOkta(data.enabled);
      } else {
        console.error('Failed to check OKTA feature flag');
        setUseOkta(false);
      }
    } catch (error) {
      console.error('Error checking OKTA feature flag:', error);
      setUseOkta(false);
    } finally {
      setOktaCheckComplete(true);
    }
  };

  const checkAnthemEmployee = async () => {
    const root = NODE_API_URL || API_HTTP_URL.replace('/graphql', '');
    const ipUrl = `${root}/get-ip`;
    return fetch(ipUrl, {
      method: 'GET',
      credentials: 'include',
    })
      .then(async response => {
        if (![401, 200].includes(response?.status)) {
          setIsSiteDown(true);
        } else {
          const jsonResponse = await response.json();
          if (jsonResponse?.ip) {
            setIsAnthemEmployee(true);
          }
        }
        setIsLoading(false);
      })
      .catch(err => {
        if (err.message.includes('GraphQL error')) {
          setError('You are not allowed to run this query or mutation');
        } else {
          setIsSiteDown(true);
        }
        setIsLoading(false);
      });
  };

  useEffect(() => {
    // First check OKTA feature flag
    checkOktaFeatureFlag();
  }, []);

  useEffect(() => {
    // Only proceed after OKTA check is complete
    if (oktaCheckComplete) {
      // Check if user just logged out - don't auto-redirect to OKTA
      const isFromLogout = searchParams.get('logout') === 'true' || 
                          sessionStorage.getItem('logoutInProgress') === 'true';
      
      if (useOkta && !isFromLogout) {
        const root = NODE_API_URL || API_HTTP_URL.replace('/graphql', '');
        const oktaLoginUrl = `${root}/okta/login`;
        window.location.href = oktaLoginUrl;
      } else {
        checkAnthemEmployee();
      }
    }
  }, [oktaCheckComplete, useOkta]);

  const renderTitle = () => {
    console.log(' App render from release/20260827 branch -20260820 date ');
    if (isLoading) {
      return '';
    } else if (isSiteDown) {
      return <Bold>The site is currently down,Please wait</Bold>;
    } else if (error) {
      return <Bold>{error}</Bold>;
    } else if (isAnthemEmployee) {
      return 'Sign in to Care Manager';
    } else if (!isAnthemEmployee) {
      return 'Sign in to Care Control';
    }
  };

  const renderPage = () => {
    if (isLoading) {
      return <Spinner loading />;
    } else if (isSiteDown) {
      return <SiteDownContainer navigate={navigate} withSiteBypass={false} />;
    } else if (error) {
      return <div>{error}</div>;
    }
    return <LoginFormContainer navigate={navigate} isAnthemEmployee={isAnthemEmployee} />;
  };

  return (
    <StandaloneFormPageTemplate title={renderTitle()}>{renderPage()}</StandaloneFormPageTemplate>
  );
};

LoginPage.propTypes = {
  history: PropTypes.object.isRequired,
};

export default LoginPage;
